/**
 * Cottage drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const cottageDrawingsPack: ToilePack = {
    slug: 'cottage-drawings',
    category: 'illustration',
    name: 'Cottage drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'thatch', label: 'Thatched cottage', subject: 'a thatched English cottage with roses round the door, a picket gate and a garden path'},
        {key: 'stone', label: 'Stone cottage', subject: 'a stone cottage in the hills with smoke rising from the chimney and a stack of firewood by the wall'},
        {key: 'woods', label: 'Cottage in snow', subject: 'a small timber cottage in the woods with one lit window at dusk and snow heavy on the roof'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil', style: 'graphite pencil drawing, soft layered shading from sharp 2H detail to deep 6B darks, visible paper tooth', palette: 'graphite gray only', framing: 'on plain white paper, the whole sheet used, a thin white margin around the drawing'},
        {key: 'ink', label: 'Pen and ink', style: 'pen and ink drawing, confident contour lines, fine crosshatching and stippling for tone', palette: 'black ink only', framing: 'on clean white paper, centered, a white margin all round'},
        {key: 'charcoal', label: 'Charcoal', style: 'charcoal drawing, smudged tonal masses with crisp accents and lifted highlights', palette: 'black charcoal on white', framing: 'on white drawing paper, the subject filling the sheet, a white edge left clear'},
    ],
    directions: ['autumn', 'a lake', 'a cat'],
    directionHint: 'try: autumn',
    seo: {
        title: 'Cottage Drawings: Thatched, Stone, Snowy Woods',
        metaDescription: 'Nine cottage drawings: a thatched cottage with roses, a stone cottage in the hills and a snowy timber cottage at dusk, in three media.',
        h1: 'Cottage drawings: a thatched cottage, a stone cottage, a cottage in snow',
        tagline: 'Three cottages, each drawn in graphite pencil, pen and ink, and charcoal.',
        intro: 'Three cottages: a thatched cottage with roses round the door, a stone cottage in the hills with smoke from the chimney, and a timber cottage in snowy woods with one lit window at dusk. Each is drawn in pencil, pen and ink, and charcoal, so the thatch, the stone and the snow can be compared on the same kind of house. Type a different house or a twist, such as autumn or a lake, and the same media carry it.',
        useCases: [
            'a sketchbook study of houses',
            'a framed print for a kitchen',
            'a card with a cottage scene',
            'a reference for thatch and stone texture',
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three cottages: a thatched cottage with roses, a stone cottage in the hills, and a timber cottage in the snow. Each appears in three media.'},
            {q: 'Can I ask for a different cottage?', a: 'Yes. Type a setting or twist, such as autumn, a lake or a cat. A typed direction is a request, and the drawing may differ from the samples.'},
            {q: 'How do the three styles differ?', a: 'Pencil builds soft layered shading on plain white paper. Pen and ink uses contour lines and crosshatching. Charcoal uses smudged tone with crisp accents.'},
            {q: 'Is each drawing wide or tall?', a: 'Wide. The cottage drawings are wider than they are tall.'},
        ],
    },
}
